import React, { useState } from "react";
import useSWR from "swr";
import {
  Table, Select, Space, Input, InputNumber, Button, Drawer, Tag,
  Descriptions, Progress, Empty, Spin, DatePicker, Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { getAssessmentResults, getAssessmentReport } from "../api/domainApi";
import { AssessmentResultListItem, AssessReportIndicator } from "../types/domain";
import PageHeader from "../components/common/PageHeader";

const { RangePicker } = DatePicker;
const PAGE_SIZE = 20;

// stageLevel(1=안정 / 2=주의 / 3=위험) → 태그색 + 막대색 + 라벨
const STAGE: Record<number, { tag: string; bar: string; label: string }> = {
  1: { tag: "green", bar: "#52c41a", label: "안정" },
  2: { tag: "gold", bar: "#faad14", label: "주의" },
  3: { tag: "red", bar: "#ff4d4f", label: "위험" },
};
const stageOf = (lv: number) => STAGE[lv] ?? { tag: "default", bar: "#bfbfbf", label: "-" };

const fmtDt = (v?: string) => (v ? v.replace("T", " ").slice(0, 16) : "-");

// 6지표 고정 축 순서 (S 사회적 반응성 / T 신체 민감도 / A 사회적 의존성 / E 환경 불안도 / I 자기조절력 부족 / V 활동성)
const AXIS_ORDER = ["S", "T", "A", "E", "I", "V"];

/** 스토어 리포트처럼 6지표를 육각형 레이더로 — 값=stageLevel(1~3), 의존성 없이 SVG. */
const IndicatorRadar: React.FC<{ indicators: AssessReportIndicator[] }> = ({ indicators }) => {
  const size = 300;
  const cx = size / 2;
  const cy = size / 2;
  const R = 88;
  const byCode = new Map(indicators.map((i) => [i.indicatorCode, i]));
  const axes: AssessReportIndicator[] = AXIS_ORDER.every((c) => byCode.has(c))
    ? AXIS_ORDER.map((c) => byCode.get(c) as AssessReportIndicator)
    : indicators;
  const n = axes.length || 1;
  const ang = (i: number) => ((-90 + (360 / n) * i) * Math.PI) / 180;
  const pt = (i: number, r: number): [number, number] => [
    cx + r * Math.cos(ang(i)),
    cy + r * Math.sin(ang(i)),
  ];
  const ringPoints = (level: number) =>
    axes.map((_, i) => pt(i, (R * level) / 3).join(",")).join(" ");
  const dataPoints = axes
    .map((a, i) => pt(i, (R * (a.stageLevel || 0)) / 3).join(","))
    .join(" ");

  return (
    <svg
      viewBox={`0 0 ${size} ${size}`}
      width="100%"
      style={{ maxWidth: 320, display: "block", margin: "4px auto 10px" }}
    >
      {[1, 2, 3].map((lv) => (
        <polygon key={lv} points={ringPoints(lv)} fill="none" stroke="var(--app-border)" strokeWidth={1} />
      ))}
      {axes.map((_, i) => {
        const [x, y] = pt(i, R);
        return <line key={i} x1={cx} y1={cy} x2={x} y2={y} stroke="var(--app-border)" strokeWidth={1} />;
      })}
      <polygon points={dataPoints} fill="rgba(250,173,20,0.30)" stroke="#faad14" strokeWidth={2} />
      {axes.map((a, i) => {
        const [px, py] = pt(i, (R * (a.stageLevel || 0)) / 3);
        const [lx, ly] = pt(i, R + 18);
        return (
          <g key={a.indicatorCode}>
            <circle cx={px} cy={py} r={3.5} fill={stageOf(a.stageLevel).bar} />
            <text x={lx} y={ly} fontSize={11} textAnchor="middle" dominantBaseline="middle" fill="var(--app-text-muted)">
              {a.indicatorName}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

const AssessmentReportPage: React.FC = () => {
  const [page, setPage] = useState(1);
  const [userId, setUserId] = useState<number | undefined>();
  const [userIdInput, setUserIdInput] = useState<number | null>(null);
  const [typeCode, setTypeCode] = useState<string | undefined>();
  const [typeInput, setTypeInput] = useState("");
  const [userKeyword, setUserKeyword] = useState<string | undefined>();
  const [userKwInput, setUserKwInput] = useState("");
  const [range, setRange] = useState<any>(null);
  const [statusFilter, setStatusFilter] = useState<"all" | "current" | "previous">("all");
  const [searchTick, setSearchTick] = useState(0); // 검색 버튼 누를 때마다 +1 → 동일 조건도 재조회
  const [selectedRsltId, setSelectedRsltId] = useState<number | null>(null);

  const from = range?.[0]?.format("YYYY-MM-DD[T]00:00:00");
  const to = range?.[1]?.format("YYYY-MM-DD[T]23:59:59");
  const current = statusFilter === "all" ? undefined : statusFilter === "current";

  const { data, isLoading } = useSWR(
    `/assessment/results?p=${page}&u=${userId}&k=${userKeyword}&c=${statusFilter}&t=${typeCode}&f=${from}&e=${to}&x=${searchTick}`,
    () => getAssessmentResults({ page: page - 1, size: PAGE_SIZE, userId, userKeyword, current, typeCode, from, to }),
    { keepPreviousData: true },
  );

  const { data: report, isLoading: reportLoading } = useSWR(
    selectedRsltId ? `/assessment/report/${selectedRsltId}` : null,
    () => getAssessmentReport(selectedRsltId!),
  );

  const columns: ColumnsType<AssessmentResultListItem> = [
    { title: "결과ID", dataIndex: "rsltId", width: 80 },
    {
      title: "구분", width: 96,
      render: (_, r) => (r.current ? <Tag color="green">현재</Tag> : <Tag>재시작 전</Tag>),
    },
    { title: "사용자", width: 160, render: (_, r) => `${r.userName ?? "—"} (#${r.userId})` },
    { title: "반려견", width: 160, render: (_, r) => `${r.petName ?? "—"} (#${r.petId})` },
    { title: "유형", ellipsis: { showTitle: true }, render: (_, r) => `${r.typeName} (${r.typeCode})` },
    { title: "평가일", dataIndex: "createdAt", width: 140, render: fmtDt },
    {
      title: "", width: 110,
      render: (_, r) => (
        <Button size="small" type="primary" onClick={() => setSelectedRsltId(r.rsltId)}>
          리포트 보기
        </Button>
      ),
    },
  ];

  const resetFilters = () => {
    setUserKeyword(undefined); setUserKwInput("");
    setUserId(undefined); setUserIdInput(null);
    setTypeCode(undefined); setTypeInput("");
    setRange(null); setStatusFilter("all"); setPage(1);
  };

  return (
    <div>
      <PageHeader title="성향평가 리포트 조회" />

      <Space style={{ marginBottom: 16 }} wrap>
        <Input
          placeholder="사용자명"
          value={userKwInput}
          onChange={(e) => setUserKwInput(e.target.value)}
          onPressEnter={() => {
            setUserKeyword(userKwInput.trim() || undefined);
            setPage(1);
            setSearchTick((t) => t + 1);
          }}
          style={{ width: 150 }}
          allowClear
        />
        <InputNumber
          placeholder="사용자 ID"
          value={userIdInput}
          onChange={(v) => setUserIdInput(v)}
          style={{ width: 120 }}
        />
        <Input
          placeholder="유형 코드"
          value={typeInput}
          onChange={(e) => setTypeInput(e.target.value)}
          style={{ width: 140 }}
        />
        <RangePicker value={range} onChange={(v) => setRange(v)} />
        <Select
          value={statusFilter}
          onChange={(v) => {
            setStatusFilter(v);
            setPage(1);
          }}
          style={{ width: 130 }}
          options={[
            { value: "all", label: "구분: 전체" },
            { value: "current", label: "현재" },
            { value: "previous", label: "재시작 전" },
          ]}
        />
        <Button
          type="primary"
          onClick={() => {
            setUserKeyword(userKwInput.trim() || undefined);
            setUserId(userIdInput ?? undefined);
            setTypeCode(typeInput.trim() || undefined);
            setPage(1);
            setSearchTick((t) => t + 1);
          }}
        >
          검색
        </Button>
        <Button onClick={resetFilters}>초기화</Button>
      </Space>

      <Table
        rowKey="rsltId"
        columns={columns}
        dataSource={data?.content}
        loading={isLoading}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total: data?.page?.totalElements,
          onChange: setPage,
          showSizeChanger: false,
        }}
        size="small"
      />

      <Drawer
        title={report ? `성향평가 리포트 — ${report.pet?.petName ?? ""}` : "성향평가 리포트"}
        width={560}
        open={selectedRsltId != null}
        onClose={() => setSelectedRsltId(null)}
      >
        {reportLoading && <div style={{ textAlign: "center", padding: 40 }}><Spin /></div>}
        {!reportLoading && !report && <Empty description="리포트를 불러올 수 없습니다." />}
        {!reportLoading && report && (
          <>
            <Descriptions
              column={1}
              size="small"
              bordered
              style={{ marginBottom: 16 }}
              items={[
                { key: "id", label: "결과 ID", children: report.rsltId },
                { key: "pet", label: "반려견", children: `${report.pet?.petName ?? "—"} (#${report.pet?.petId})` },
                { key: "user", label: "사용자", children: (() => {
                    const u = data?.content?.find((x) => x.rsltId === report.rsltId);
                    return u ? `${u.userName ?? "—"} (#${u.userId})` : "-";
                  })() },
                { key: "date", label: "평가일", children: fmtDt(report.createdAt) },
              ]}
            />

            <Typography.Title level={5} style={{ marginTop: 0 }}>
              {report.type?.typeName}{" "}
              <Typography.Text type="secondary">({report.type?.typeCode})</Typography.Text>
            </Typography.Title>
            {report.type?.subName && (
              <Typography.Paragraph type="secondary" style={{ marginTop: -4 }}>
                {report.type.subName}
              </Typography.Paragraph>
            )}
            {report.type?.group && (
              <Tag color="blue" style={{ marginBottom: 16 }}>
                {report.type.group.groupName} · {report.type.group.description}
              </Tag>
            )}

            <Typography.Title level={5}>지표 ({report.indicators?.length ?? 0})</Typography.Title>
            {report.indicators && report.indicators.length > 0 && (
              <IndicatorRadar indicators={report.indicators} />
            )}
            {report.indicators?.map((ind) => {
              const s = stageOf(ind.stageLevel);
              return (
                <div key={ind.indicatorCode} style={{ marginBottom: 14 }}>
                  <Space size={6} wrap>
                    <b>{ind.indicatorName}</b>
                    <Typography.Text type="secondary">({ind.indicatorCode})</Typography.Text>
                    <Tag color={s.tag}>{ind.stageName ?? s.label}</Tag>
                    {ind.riskTriggered && <Tag color="red">⚠️ 위험규칙</Tag>}
                  </Space>
                  <Progress
                    percent={Math.round((ind.stageLevel / 3) * 100)}
                    showInfo={false}
                    strokeColor={s.bar}
                    size="small"
                    style={{ margin: "4px 0" }}
                  />
                  <Typography.Text style={{ fontSize: 13 }}>{ind.description}</Typography.Text>
                </div>
              );
            })}

            {report.extras && report.extras.length > 0 && (
              <>
                <Typography.Title level={5}>추가 질문</Typography.Title>
                {report.extras.map((ex) => {
                  const s = stageOf(ex.stageLevel);
                  return (
                    <div key={ex.extraCode} style={{ marginBottom: 12 }}>
                      <Space size={6}>
                        <b>{ex.extraLabel}</b>
                        <Tag color={s.tag}>{ex.stageName ?? s.label}</Tag>
                      </Space>
                      <div style={{ fontSize: 13 }}>{ex.description}</div>
                    </div>
                  );
                })}
              </>
            )}
          </>
        )}
      </Drawer>
    </div>
  );
};

export default AssessmentReportPage;
